<template>
  <article class="invoice-create-card invoice-create-card--accent-billing payment-receipt-document-card">
    <header class="invoice-create-card__head invoice-create-card__head--space">
      <h3>
        <i class="fa fa-user-o invoice-create-section-icon invoice-create-section-icon--billing" aria-hidden="true"></i>
        {{ text?.create?.ui?.documentCustomerCard?.title || 'Documento / Cliente' }}
      </h3>

      <div class="payment-receipt-document-card__currency-wrap">
        <AppCurrencyControl
          v-model="currencyModel"
          :exchange-rate="exchangeRate"
          @update:exchange-rate="$emit('update:exchange-rate', $event)"
        />
      </div>
    </header>

    <div class="invoice-create-info-grid payment-receipt-document-card__grid">
      <div class="invoice-create-field app-modal-field payment-receipt-document-card__voucher-field">
        <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.voucherType || 'Tipo de comprobante' }}</span>
        <div class="payment-receipt-document-card__voucher-toggle">
          <label class="app-modal-toggle payment-receipt-document-card__voucher-item">
            <input
              type="checkbox"
              :checked="isCfdiMode"
              @change="setVoucherType('cfdi')"
            />
            <span>CFDI</span>
          </label>

          <label class="app-modal-toggle payment-receipt-document-card__voucher-item">
            <input
              type="checkbox"
              :checked="!isCfdiMode"
              @change="setVoucherType('simple_note')"
            />
            <span>Nota Simple</span>
          </label>
        </div>
      </div>

      <div class="invoice-create-field app-modal-field">
        <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.customer || 'Cliente' }}</span>
        <multiselect
          v-model="selectedCustomerModel"
          :options="customerOptions"
          track-by="value"
          label="label"
          :custom-label="customerLabel"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select"
          :placeholder="text?.create?.ui?.documentCustomerCard?.placeholders?.customer || 'Selecciona cliente'"
        >
          <template #option="{ option }">
            <div class="payment-receipt-document-card__customer-option">
              <strong>{{ option?.label || '-' }}</strong>
              <small>{{ text?.create?.ui?.documentCustomerCard?.customerPendingPrefix || 'Pendiente' }} {{ currency }}: {{ formatMoney(resolveCustomerPending(option)) }}</small>
            </div>
          </template>
          <template #singleLabel="{ option }">
            <div class="payment-receipt-document-card__customer-option payment-receipt-document-card__customer-option--single">
              <strong>{{ option?.label || '-' }}</strong>
            </div>
          </template>
          <template #noResult>
            <span>{{ text?.create?.ui?.documentCustomerCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.documentCustomerCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <div class="invoice-create-field app-modal-field payment-receipt-document-card__pending-wrap">
        <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.pendingAmount || 'Monto Pendiente' }}</span>
        <strong class="payment-receipt-document-card__pending-amount">
          {{ formatMoney(customerPendingAmount) }}
        </strong>
      </div>
    </div>

    <div v-if="isCfdiMode" class="payment-receipt-document-card__fiscal-section">
      <div class="invoice-create-field app-modal-field">
        <div class="payment-receipt-document-card__fiscal-head">
          <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.fiscalData || 'Datos fiscales' }}</span>
          <button
            type="button"
            class="payment-receipt-document-card__fiscal-toggle"
            @click="showFiscalDetails = !showFiscalDetails"
          >
            <i class="fa" :class="showFiscalDetails ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
            {{ showFiscalDetails
              ? (text?.create?.ui?.documentCustomerCard?.actions?.hide || 'Ocultar')
              : (text?.create?.ui?.documentCustomerCard?.actions?.showAll || 'Ver todo') }}
          </button>
        </div>

        <multiselect
          v-model="selectedReceiverModel"
          :options="contributorOptions"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          class="app-modal-select invoice-create-receiver-select"
          :placeholder="text?.create?.ui?.documentCustomerCard?.placeholders?.receiver || 'Selecciona dato fiscal'"
        >
          <template #option="{ option }">
            <div class="expense-supplier-option">
              <small><strong>RFC:</strong> {{ option?.meta?.rfc || '-' }}</small>
              <small><strong>Razón social:</strong> {{ option?.meta?.businessName || '-' }}</small>
            </div>
          </template>
          <template #singleLabel="{ option }">
            <div class="expense-supplier-option invoice-create-receiver-single">
              <span>{{ buildSelectedReceiverLabel(option) }}</span>
            </div>
          </template>
          <template #noResult>
            <span>{{ text?.create?.ui?.documentCustomerCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
          <template #noOptions>
            <span>{{ text?.create?.ui?.documentCustomerCard?.emptyOption || 'Sin opción disponible' }}</span>
          </template>
        </multiselect>
      </div>

      <div v-if="showFiscalDetails" class="invoice-create-receiver-preview">
        <template v-if="selectedReceiver">
          <button
            type="button"
            class="app-table-icon-btn app-table-icon-btn--edit invoice-create-receiver-preview-edit-btn"
            :title="text?.create?.ui?.documentCustomerCard?.actions?.editFiscalData || 'Editar dato fiscal'"
            @click="openContributorModal"
          >
            <i class="fa fa-pencil" aria-hidden="true"></i>
          </button>

          <div class="invoice-create-receiver-preview-grid">
            <div class="invoice-create-receiver-kv">
              <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.customerLabel || 'Cliente' }}</span>
              <strong>{{ selectedReceiver?.meta?.clientName || selectedCustomer?.label || '-' }}</strong>
            </div>

            <div class="invoice-create-receiver-kv">
              <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.rfc || 'RFC' }}</span>
              <strong>{{ selectedReceiver?.meta?.rfc || '-' }}</strong>
            </div>

            <div class="invoice-create-receiver-kv">
              <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.businessName || 'Razón social' }}</span>
              <strong>{{ selectedReceiver?.meta?.businessName || '-' }}</strong>
            </div>

            <div class="invoice-create-receiver-kv">
              <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.taxRegime || 'Régimen fiscal' }}</span>
              <strong>{{ selectedReceiver?.meta?.taxRegime || '-' }}</strong>
            </div>

            <div class="invoice-create-receiver-kv">
              <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.cfdiUse || 'Uso CFDI' }}</span>
              <strong>{{ selectedReceiver?.meta?.cfdiUse || '-' }}</strong>
            </div>

            <div class="invoice-create-receiver-kv">
              <span>{{ text?.create?.ui?.documentCustomerCard?.fields?.postalCode || 'Código postal' }}</span>
              <strong>{{ selectedReceiver?.meta?.fiscalResidence || '-' }}</strong>
            </div>
          </div>
        </template>

        <span v-else>{{ text?.create?.ui?.documentCustomerCard?.emptyState || 'Selecciona un dato fiscal para visualizar su información.' }}</span>
      </div>
    </div>

    <ContributorModal
      :show="showContributorModal"
      :mode="contributorModalMode"
      :initial-data="contributorModalInitialData"
      :state-options="[]"
      :regimen-options="taxRegimeOptions"
      :uso-cfdi-options="cfdiUseOptions"
      :payment-method-options="paymentFormOptions"
      :payment-method-type-options="paymentMethodTypeOptions"
      :loading="contributorModalLoading"
      @close="closeContributorModal"
      @submit="onContributorModalSubmit"
    />
  </article>
</template>

<script>
import AppCurrencyControl from '@/components/common/AppCurrencyControl.vue';
import ContributorModal from '@/components/contributors/ContributorModal.vue';
import invoicesService from '@/services/invoices';
import customersService from '@/services/customers';
import { extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';

export default {
  name: 'PaymentReceiptCreateDocumentCustomerCard',
  components: {
    AppCurrencyControl,
    ContributorModal,
  },
  props: {
    text: {
      type: Object,
      default: () => ({}),
    },
    documentTypeOptions: {
      type: Array,
      default: () => [],
    },
    customerOptions: {
      type: Array,
      default: () => [],
    },
    selectedCustomer: {
      type: Object,
      default: null,
    },
    contributorOptions: {
      type: Array,
      default: () => [],
    },
    taxRegimeOptions: {
      type: Array,
      default: () => [],
    },
    cfdiUseOptions: {
      type: Array,
      default: () => [],
    },
    paymentMethodTypeOptions: {
      type: Array,
      default: () => [],
    },
    paymentFormOptions: {
      type: Array,
      default: () => [],
    },
    selectedReceiver: {
      type: Object,
      default: null,
    },
    info: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    exchangeRate: {
      type: Number,
      default: 1,
    },
    customerPendingAmount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['select-customer', 'select-receiver', 'info-change', 'update:currency', 'update:exchange-rate', 'edit-contributor'],
  data() {
    return {
      showContributorModal: false,
      contributorModalLoading: false,
      contributorModalMode: 'edit',
      contributorModalInitialData: null,
      showFiscalDetails: false,
    };
  },
  computed: {
    isCfdiMode() {
      return String(this.info?.voucherType || 'cfdi').trim().toLowerCase() !== 'simple_note';
    },
    selectedCustomerModel: {
      get() {
        return this.selectedCustomer;
      },
      set(value) {
        this.$emit('select-customer', value || null);
      },
    },
    selectedReceiverModel: {
      get() {
        return this.selectedReceiver;
      },
      set(value) {
        this.$emit('select-receiver', value || null);
      },
    },
    currencyModel: {
      get() {
        return this.currency;
      },
      set(value) {
        this.$emit('update:currency', String(value || '').trim().toUpperCase() || 'MXN');
      },
    },
  },
  methods: {
    setVoucherType(type) {
      this.$emit('info-change', { key: 'voucherType', value: String(type || 'cfdi').trim().toLowerCase() });
    },
    resolveCustomerPending(option) {
      const pendingByCurrency = option?.meta?.pendingByCurrency && typeof option.meta.pendingByCurrency === 'object'
        ? option.meta.pendingByCurrency
        : {};
      return Number(pendingByCurrency?.[this.currency] || 0) || 0;
    },
    customerLabel(option) {
      const amount = this.resolveCustomerPending(option);
      const name = String(option?.label || '').trim();
      return `${name} - ${this.formatMoney(amount)}`;
    },
    buildSelectedReceiverLabel(option) {
      const rfc = String(option?.meta?.rfc || '').trim();
      const businessName = String(option?.meta?.businessName || option?.label || '').trim();

      if (rfc !== '' && businessName !== '') {
        return `${rfc} - ${businessName.toUpperCase()}`;
      }

      if (businessName !== '') {
        return businessName.toUpperCase();
      }

      return '-';
    },
    formatMoney(amount) {
      return formatCurrency(amount, { currency: this.currency });
    },
    async openContributorModal() {
      const customerId = Number(this.selectedCustomer?.value || this.selectedCustomer?.meta?.customerId || 0) || 0;
      const contributorId = Number(this.selectedReceiver?.value || this.selectedReceiver?.meta?.id || 0) || 0;

      if (customerId <= 0 || contributorId <= 0) {
        return;
      }

      this.contributorModalLoading = true;
      try {
        const response = await invoicesService.contributorShow(contributorId);
        const payload = extractPayload(response);

        this.contributorModalInitialData = payload && typeof payload === 'object' ? { ...payload } : null;
        this.showContributorModal = true;
      } catch (error) {
        // no-op
      } finally {
        this.contributorModalLoading = false;
      }
    },
    closeContributorModal() {
      this.showContributorModal = false;
      this.contributorModalLoading = false;
      this.contributorModalInitialData = null;
      this.contributorModalMode = 'edit';
    },
    async onContributorModalSubmit(payload) {
      const customerId = Number(this.selectedCustomer?.value || this.selectedCustomer?.meta?.customerId || 0) || 0;
      const contributorId = Number(this.selectedReceiver?.value || this.selectedReceiver?.meta?.id || 0) || 0;

      if (customerId <= 0 || contributorId <= 0) {
        return;
      }

      this.contributorModalLoading = true;
      try {
        await customersService.contributorsUpdate(customerId, contributorId, payload || {});
        this.closeContributorModal();
        this.$emit('edit-contributor');
      } catch (error) {
        this.contributorModalLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-receipt-document-card {
  display: grid;
  gap: 8px;
  align-content: start;
  padding-top: 10px;
}

.payment-receipt-document-card .invoice-create-card__head {
  align-items: flex-start;
  margin-bottom: 6px;
}

.payment-receipt-document-card__currency-wrap {
  width: 230px;
  max-width: 100%;
  display: flex;
  justify-content: flex-end;
}

.payment-receipt-document-card__grid {
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1.15fr) minmax(210px, 0.8fr);
}

.payment-receipt-document-card__voucher-toggle {
  display: flex;
  gap: 10px;
  min-height: 40px;
  align-items: center;
}

.payment-receipt-document-card__voucher-item {
  margin: 0;
}

.payment-receipt-document-card__voucher-item span {
  white-space: nowrap;
}

.payment-receipt-document-card__customer-option {
  display: grid;
  gap: 1px;
}

.payment-receipt-document-card__customer-option strong {
  font-size: 12px;
  color: #0f172a;
}

.payment-receipt-document-card__customer-option small {
  font-size: 11px;
  color: #64748b;
}

.payment-receipt-document-card__customer-option--single small {
  display: none;
}

.payment-receipt-document-card__pending-wrap {
  border: 0;
  border-radius: 0;
  padding: 0;
  background: transparent;
  width: 100%;
  max-width: 230px;
  justify-self: end;
}

.payment-receipt-document-card__pending-amount {
  display: block;
  margin-top: 4px;
  font-size: 18px;
  color: #0f172a;
  text-align: right;
}

.payment-receipt-document-card__fiscal-section {
  border-top: 1px solid rgba(148, 163, 184, 0.32);
  padding-top: 10px;
  display: grid;
  gap: 10px;
}

.payment-receipt-document-card__fiscal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.payment-receipt-document-card__fiscal-toggle {
  border: 0;
  background: transparent;
  color: #0070cd;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

@media (max-width: 1200px) {
  .payment-receipt-document-card__grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .payment-receipt-document-card__grid {
    grid-template-columns: 1fr;
  }

  .payment-receipt-document-card__currency-wrap {
    width: 100%;
    max-width: 100%;
  }
}
</style>
